import React, { useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Program } from '../../Reports/NewReportDialog';
import { InvestmentSplitWithAnswers } from './utils';
import { AllocationReports } from './allocationLocalStorage';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { faClone, faFileCirclePlus } from '@fortawesome/pro-light-svg-icons';
import { MainCard } from '../../ReportBuilder/Components/Beneficiary/BeneficiaryMainCard';
import AllocationCloneReport from './AllocationCloneReport';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';

const cloneExistingReportTooltip = (
  <>
    Clone Exiting report (s)- Create new reports by copying past reports-To
    create a new report — representing a new program/funding term — by copying
    (duplicating) an existing report
  </>
);
const newReportTooltip = (
  <>
    Create a brand new report-to create one or more new reports from scratch,
    provide the program/initiative name
  </>
);

export interface AllocationReportCreationProps {
  totalAmount: number;
  allPrograms: Program[];
  allReports: {
    [programId: number]: {
      [year: number]: {
        dbId: number;
        endDate: string | null; // "2025-11-20"
        program: { name: string };
        programId: number;
        year: string; // '2024'
      };
    };
  };
  reports: AllocationReports;
  investmentFinalSplitIndex: number;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  investment: Required<InvestmentSplitWithAnswers>;
  selectedProgram: Program | undefined;
  handleNext: (report?: {
    year: number;
    reportId: number;
    programName: string;
  }) => unknown;
  handleBack: () => unknown;
  setReports: React.Dispatch<React.SetStateAction<AllocationReports>>;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.large};
  .create-report-choose {
    margin-bottom: ${SPACERS.tiny};
  }
  .create-report-heading {
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.medium};
  }
  .clone-report-wrapper {
    display: flex;
    gap: ${SPACERS.small};
    justify-content: center;
    margin-bottom: ${SPACERS.medium};
    .clone-row-item {
      width: 232px;
    }
  }
  .create-new-report-wrapper {
    text-align: center;
    .new-report-great {
      margin-bottom: ${SPACERS.tiny};
    }
    button {
      min-width: 150px;
      margin: ${SPACERS.medium} auto;
    }
  }
`;

const AllocationReportCreation: React.FC<AllocationReportCreationProps> = ({
  reports,
  investment,
  allReports,
  allPrograms,
  totalAmount,
  programsList,
  investmentFinalSplitIndex,
  selectedProgram,
  handleNext,
  handleBack,
  setReports,
}) => {
  const investmentYear = Number(investment.year);
  const [reportType, setReportType] = useState<'clone' | 'new' | undefined>(
    undefined,
  );
  const reportId =
    reports[investment.program.name]?.[investmentYear] ||
    allReports[investment.program.Id]?.[investmentYear]?.dbId;

  if (reportId) {
    handleNext({
      reportId,
      year: investmentYear,
      programName: investment.program.name,
    });
    return null;
  }

  return (
    <StyledDiv>
      <TrueImpactText
        variant="heading-subheading"
        align="center"
        className="create-report-choose"
      >
        It looks like this program year doesn’t have a report created yet.
      </TrueImpactText>
      <TrueImpactText variant="body-large" align="center">
        Choose one of the options below and we will get that report created for
        you!
      </TrueImpactText>
      <TrueImpactText
        variant="heading-subheading"
        align="center"
        className="create-report-heading"
      >
        Now let’s create your first report for this program.
      </TrueImpactText>
      <div className="clone-report-wrapper">
        <MainCard
          icon={faClone}
          onClick={() => setReportType('clone')}
          selected={reportType === 'clone'}
          text="Clone existing report"
          className="clone-row-item"
          tooltipComponent={cloneExistingReportTooltip}
        />
        <MainCard
          icon={faFileCirclePlus}
          onClick={() => setReportType('new')}
          selected={reportType === 'new'}
          text="Create a new report"
          className="clone-row-item"
          tooltipComponent={newReportTooltip}
        />
      </div>
      {reportType === 'new' && (
        <div className="create-new-report-wrapper">
          <TrueImpactText variant="body-bold" className="new-report-great">
            Great!
          </TrueImpactText>
          <TrueImpactText>
            We’ll get you set up with a fresh report you can build from scratch!
          </TrueImpactText>
          <TrueImpactButton onClick={() => handleNext()}>Next</TrueImpactButton>
        </div>
      )}
      {reportType === 'clone' && (
        <AllocationCloneReport
          reports={reports}
          programsList={programsList}
          selectedYear={investmentYear}
          selectedProgram={investment.program}
          handleNext={handleNext}
          investmentFinalSplitIndex={investmentFinalSplitIndex}
          setReports={setReports}
        />
      )}
    </StyledDiv>
  );
};

export default AllocationReportCreation;
